import type { Location } from "history";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Hook that returns a function to update queryState in the URL.
 *
 * Converts a QueryState object into flat URL search params.
 *
 * @param dehydrateQueryState - Function to convert QueryState to URLSearchParams
 * @returns Function to update the queryState in the URL
 */
export function useUpdateQueryState<T>(
  dehydrateQueryState: (opts: {
    queryState: T;
    location: Location;
  }) => URLSearchParams,
) {
  const location = useLocation();
  const navigate = useNavigate();

  return useCallback(
    (queryState: T, options?: { replace?: boolean }) => {
      const searchParams = dehydrateQueryState({
        queryState,
        location,
      });
      const search = `?${searchParams}`;
      navigate(search, { replace: options?.replace ?? true });
    },
    [location, navigate, dehydrateQueryState],
  );
}
